import {
  StoragePathType,
  useGenerateAiTutorFromTitleMutation,
  useGenerateEvalCriteriaMutation,
  useGenerateObjectiveImagePromptsMutation,
  useGetGcpSignedUrlForReadLazyQuery,
  useGetVisualizationQuery,
} from "@axio/graphql/generated/graphql";
import useFileUpload, { generatePublicUrl } from "../useFileUpload";
import useObjectiveImageGeneration from "./useObjectiveImageGeneration";

export type { CompletedObjectiveImageJob } from "./useObjectiveImageGeneration";

type UseInteractiveLearningDataParams = {
  visualizationId: string | null;
  currentDomain?: string;
  onUploadError?: (message?: string) => void;
};

type UploadedFileRef = {
  id: string;
  filePath?: string | null;
};

const useInteractiveLearningData = ({
  visualizationId,
  currentDomain,
  onUploadError,
}: UseInteractiveLearningDataParams) => {
  const [generateAiTutorFromTitle] = useGenerateAiTutorFromTitleMutation();
  const [generateEvalCriteria] = useGenerateEvalCriteriaMutation();

  const [generateObjectiveImagePromptsMutation] = useGenerateObjectiveImagePromptsMutation();
  const { requestObjectiveImageGeneration, requestObjectiveImageRegeneration } = useObjectiveImageGeneration();
  const [getGcpSignedUrlForRead] = useGetGcpSignedUrlForReadLazyQuery();

  const {
    data: visualizationData,
    startPolling,
    stopPolling,
    refetch: refetchVisualization,
  } = useGetVisualizationQuery({
    variables: { id: visualizationId ?? "" },
    skip: !visualizationId,
    fetchPolicy: "network-only",
  });

  const { handleFileUpload: handleObjectiveImageUpload } = useFileUpload({
    storagePath: StoragePathType.Thumbnails,
    isPublic: true,
    onError: onUploadError,
  });

  const resolveUploadedFileUrl = async (file: UploadedFileRef) => {
    const fallbackUrl = currentDomain && file.filePath ? generatePublicUrl(currentDomain, file.filePath) : null;
    if (fallbackUrl) return fallbackUrl;

    const signedResult = await getGcpSignedUrlForRead({
      variables: { fileId: file.id, responseDisposition: "inline" },
    });

    return signedResult.data?.getGcpSignedUrlForRead?.data?.[0]?.signedUrl ?? null;
  };

  return {
    generateAiTutorFromTitle,
    generateEvalCriteria,
    visualizationData,
    startPolling,
    stopPolling,
    refetchVisualization,
    generateObjectiveImagePromptsMutation,
    requestObjectiveImageGeneration,
    requestObjectiveImageRegeneration,
    handleObjectiveImageUpload,
    resolveUploadedFileUrl,
  };
};

export default useInteractiveLearningData;
